import { useEffect, useState, ChangeEvent } from "react";
import { Params, useParams, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ThemeContainer } from "../../../common/containers/theme/theme.container";
import {
    fetchRecognitionResult,
    saveRecognitionResult
} from "../../../api/admin/recognition-result/endpoints-referrers";
import { fetchLearningObject } from "../../../api/admin/content-providers/endpoints-referrers";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import { LoaderView } from "../../../components/loader/loader.comp";
import { RRSelectionFilter } from "../../../components/admin/rr-selection-filter/rr-selection-filter.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import FeedbackView from "../feedback/feedback";

const RecognitionResultView = () => {
    const navigate = useNavigate();
    const { providerId, uid } = useParams<Params<string>>();
    const { user } = FirebaseAuthContainer.useContainer();
    const { isTrial } = ThemeContainer.useContainer();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [recognitionResult, setRecognitionResult] = useState<RecognitionResult>();
    const [rrText, setRrText] = useState<string>();
    const [rrList, setRrList] = useState<RecognitionResult[]>([]);
    const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(true);

        if (!user || !uid) return;

        fetchRecognitionResult(user.companyId, uid).then(response => {
            setRecognitionResult(response.data);
        });
    }, [user, uid]);

    useEffect(() => {
        if (isTrial) {
            setIsLoading(false);
            return;
        }
        if (!user || !recognitionResult) return;

        fetchLearningObject(user.companyId, recognitionResult.courseId, recognitionResult.loItemId)
            .then(response => {
                setRrList(response.data.recognitionResults);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [isTrial, user, recognitionResult]);

    useEffect(() => {
        setRrText(recognitionResult?.recognitionResultData?.loItemText ?? "");
    }, [recognitionResult]);

    const handleBackClick = () => {
        if (recognitionResult) {
            const link = !isTrial
                ? `/knowledge-base/${providerId}/${recognitionResult.courseId}`
                : `/knowledge-base`;
            navigate(link);
        }
    };

    const handleChangeSelectedRR = (rr: RecognitionResult) => {
        if (!providerId) return;
        navigate(`/recognition-result/${providerId}/${rr.uid}`);
    };

    const handleCompareClick = () => {
        if (!recognitionResult || !providerId) return;
        navigate(`/compare-recognition-result/${providerId}/${recognitionResult.uid}`);
    };

    const handleSaveClick = () => {
        if (!user || !uid) return;

        setIsLoading(true);
        saveRecognitionResult(user.companyId, uid, {
            loItemText: rrText ?? "",
            summary: recognitionResult?.recognitionResultData?.summary ?? "",
            isPublish: true
        })
            .then(response => {
                const {
                    data: { uid }
                } = response;
                navigate(`/recognition-result/${providerId}/${uid}`);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setRrText(event.target.value);
    };

    const handleFeedbackOpen = () => {
        setFeedbackOpen(true);
    };

    const handleFeedbackClose = () => {
        setFeedbackOpen(false);
    };

    return (
        <HeaderLayout
            subheaderHeight={46}
            subheader={
                <div className="rr-header">
                    <div className="rr-header-title">
                        <span className="rr-header-title__text">Review AI Result</span>
                    </div>
                    {!isTrial && (
                        <div className="rr-header-filter">
                            <RRSelectionFilter
                                list={rrList}
                                selectedItem={recognitionResult}
                                setSelectedItem={handleChangeSelectedRR}
                                rightPosition={true}
                                maxWidth={600}
                            />
                        </div>
                    )}
                </div>
            }
            footerHeight={60}
            footer={
                <div
                    style={{ height: 60 }}
                    className={`rr-footer${isTrial ? " rr-footer--trial" : ""}`}
                >
                    <div className="rr-footer-left">
                        <Button
                            className="secondary-button"
                            variant="outlined"
                            onClick={handleBackClick}
                        >
                            Close
                        </Button>
                        {!isTrial && (
                            <Button
                                className="primary-button rr-footer__button--margin-left"
                                variant="outlined"
                                disabled={
                                    recognitionResult?.recognitionResultData?.loItemText === rrText
                                }
                                onClick={handleSaveClick}
                            >
                                Save
                            </Button>
                        )}
                    </div>
                    <div className="rr-footer-right">
                        {!isTrial ? (
                            <>
                                <Button
                                    className="primary-button"
                                    variant="outlined"
                                    disabled={rrList.length < 2}
                                    onClick={handleCompareClick}
                                >
                                    Compare
                                </Button>
                            </>
                        ) : (
                            <>
                                <span className="rr-footer__message">
                                    You can&apos;t edit results in trial version.
                                </span>
                                <a className="rr-footer__link" onClick={handleFeedbackOpen}>
                                    Upgrade to full version
                                </a>
                            </>
                        )}
                    </div>
                </div>
            }
        >
            <div className="rr-view">
                {/* {recognitionResult && (
                    <div className="rr-title">
                        <span className="rr-title__text">
                            ver.{recognitionResult.version}&nbsp;
                            {recognitionResult.publisherFullName ?? "Auto"}&nbsp;
                            {formatStringDate(
                                recognitionResult.createdAt,
                                "MM.dd.yyyy — hh:mm"
                            )}
                        </span>
                    </div>
                )} */}
                <div className="rr-content">
                    <textarea
                        className="rr-content__textarea"
                        onChange={handleTextChange}
                        value={rrText}
                    />
                </div>
                <LoaderView show={isLoading} />
                <ModalTemplate
                    open={feedbackOpen}
                    width={654}
                    height={497}
                    handleClose={handleFeedbackClose}
                >
                    <FeedbackView handleClose={handleFeedbackClose} />
                </ModalTemplate>
            </div>
        </HeaderLayout>
    );
};

export default RecognitionResultView;
